import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../theme';

function StatCard({ label, value, loading }) {
  return (
    <div style={{
      backgroundColor: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '8px',
      padding: '24px',
    }}>
      <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '8px' }}>
        {label}
      </div>
      <div style={{ fontSize: '32px', fontWeight: '700', color: COLORS.textPrimary, lineHeight: 1 }}>
        {loading ? <Skeleton width="60px" height="32px" /> : value}
      </div>
      <div style={{
        height: '3px',
        width: '40px',
        backgroundColor: COLORS.accent,
        borderRadius: '2px',
        marginTop: '12px',
      }} />
    </div>
  );
}

function Skeleton({ width = '100%', height = '16px' }) {
  return (
    <div style={{
      width,
      height,
      backgroundColor: COLORS.surfaceHover,
      borderRadius: '4px',
      animation: 'pulse 1.4s ease-in-out infinite',
    }} />
  );
}

function UserListCard({ title, users, loading }) {
  return (
    <div style={{
      backgroundColor: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '8px',
      padding: '20px',
    }}>
      <div style={{
        fontSize: '14px',
        fontWeight: '600',
        color: COLORS.textPrimary,
        borderBottom: `1px solid ${COLORS.border}`,
        paddingBottom: '12px',
        marginBottom: '12px',
      }}>
        {title}
      </div>

      {loading ? (
        Array.from({ length: 3 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${COLORS.border}80` }}>
            <div style={{ flex: 1 }}>
              <Skeleton width="120px" height="13px" />
              <div style={{ marginTop: '4px' }}><Skeleton width="160px" height="11px" /></div>
            </div>
            <Skeleton width="60px" height="12px" />
          </div>
        ))
      ) : users.length === 0 ? (
        <div style={{ fontSize: '13px', color: COLORS.textMuted, padding: '8px 0' }}>No data yet.</div>
      ) : (
        users.map((u, i) => (
          <div key={u.id} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0',
            borderBottom: i < users.length - 1 ? `1px solid ${COLORS.border}80` : 'none',
          }}>
            <div>
              <div style={{ fontSize: '13px', color: COLORS.textPrimary, fontWeight: '500' }}>
                {u.displayName || u.email || 'Unknown User'}
              </div>
              <div style={{ fontSize: '12px', color: COLORS.textMuted, marginTop: '1px' }}>
                {u.email || '—'}
              </div>
            </div>
            <div style={{ fontSize: '12px', color: COLORS.textSecondary, flexShrink: 0 }}>
              {u.createdAt?.toDate
                ? u.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—'}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/**
 * P1-f / T85 item 4 (Prompt #565; #542 step 5 b-admin): test accounts/jobs
 * never inflate the dashboard counts. A doc is test only if isTestData is
 * literally true (same rule as functions/src/utils/test-data.ts).
 * Returns { real, test } counts for a snapshot's docs.
 */
export function splitTestCounts(docs) {
  let test = 0;
  for (const d of docs) if (d.data()?.isTestData === true) test++;
  return { real: docs.length - test, test };
}

/** "N" or "N (+T test)" for a { real, test } pair. */
export function formatCountWithTest({ real, test }) {
  return test > 0 ? `${real} (+${test} test)` : String(real);
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ employers: { real: 0, test: 0 }, workers: { real: 0, test: 0 }, activeJobs: { real: 0, test: 0 }, revenue: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [recentEmployers, setRecentEmployers] = useState([]);
  const [recentWorkers, setRecentWorkers] = useState([]);
  const [listsLoading, setListsLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) return;
    const unsubs = [];

    unsubs.push(onSnapshot(query(collection(db, 'users'), where('role', '==', 'employer')), snap => {
      setStats(prev => ({ ...prev, employers: splitTestCounts(snap.docs) }));
      setStatsLoading(false);
    }));

    unsubs.push(onSnapshot(query(collection(db, 'users'), where('role', '==', 'candidate')), snap => {
      setStats(prev => ({ ...prev, workers: splitTestCounts(snap.docs) }));
    }));

    unsubs.push(onSnapshot(query(collection(db, 'jobs'), where('status', '==', 'active')), snap => {
      setStats(prev => ({ ...prev, activeJobs: splitTestCounts(snap.docs) }));
    }));

    unsubs.push(onSnapshot(query(collection(db, 'payments'), where('status', '==', 'succeeded')), snap => {
      const total = snap.docs.reduce((sum, d) => sum + (d.data().amount || 0), 0);
      setStats(prev => ({ ...prev, revenue: total }));
    }));

    unsubs.push(onSnapshot(
      query(collection(db, 'users'), where('role', '==', 'employer')),
      snap => {
        setRecentEmployers(
          snap.docs.map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0))
            .slice(0, 5)
        );
        setListsLoading(false);
      }
    ));

    unsubs.push(onSnapshot(
      query(collection(db, 'users'), where('role', '==', 'candidate')),
      snap => {
        setRecentWorkers(
          snap.docs.map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0))
            .slice(0, 5)
        );
        setListsLoading(false);
      }
    ));

    return () => unsubs.forEach(u => u());
  }, [user?.uid]);

  const formatCurrency = (cents) => {
    const dollars = Math.floor(cents / 100);
    return '$' + dollars.toLocaleString('en-US');
  };

  return (
    <>
      <style>{`@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }`}</style>

      {/* Stats grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '32px',
      }}>
        <StatCard label="Total Employers" value={formatCountWithTest(stats.employers)} loading={statsLoading} />
        <StatCard label="Total Workers" value={formatCountWithTest(stats.workers)} loading={statsLoading} />
        <StatCard label="Active Jobs" value={formatCountWithTest(stats.activeJobs)} loading={statsLoading} />
        <StatCard label="Total Revenue" value={formatCurrency(stats.revenue)} loading={statsLoading} />
      </div>

      {/* Recent lists */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <UserListCard title="Recent Employers" users={recentEmployers} loading={listsLoading} />
        <UserListCard title="Recent Workers" users={recentWorkers} loading={listsLoading} />
      </div>
    </>
  );
}
